import { css } from '@emotion/react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { CompanySearch } from '../../../components/CompanySearch/CompanySearch';
import { ICompanySearchResponseDataModel } from '../../../data-models/company-search-result.data-model';
import {
  PreviewMember,
  PreviewRemovedRound,
  PreviewRound,
  PreviewTransaction,
  RoundCreationSettingsUpdate,
  RoundMatchMode,
  RoundPreviewResponse,
} from '../../../data-models/round-creation-settings.data-model';
import { CompanySearchResponseWithRealId } from '../../../schemas/CompanySearchResponse.schema';
import { getErrorMessage } from '../../../services/queryHelpers';
import { FMT } from '../../../util/formatter-service';
import { getRoundLabel } from '../../../util/preference-utils';
import { deltaLabel } from './roundCreationCopy';
import { pillCss, pillWarnCss } from './roundCreationStyles';
import { eyebrowCss, helpCss } from './RoundCreationSettingControls';
import { emptyStateLabel, exclusionReasonChip, exclusionReasonLabel, previewCopy } from './roundPreviewCopy';
import { useRoundCreationPreview } from './useRoundCreationPreview';

/**
 * The prototype's "Preview impact" modal: one row per round the rules would produce, totalled, with
 * everything the rules leave behind listed underneath. The table is the point — a count alone never
 * settles an argument about whether a grouping is right.
 */

const numericCss = css`
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
`;

const tableWrapCss = css`
  border: 1px solid var(--mui-colors-border-card);
  border-radius: 8px;
  /* Wide content scrolls inside its own box; the dialog itself must never scroll sideways. */
  overflow-x: auto;
`;

const tableCss = css`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    ${eyebrowCss};
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid var(--mui-colors-border-card);
    white-space: nowrap;
  }

  td {
    padding: 8px 10px;
    border-bottom: 1px solid var(--mui-colors-neutral-5);
  }

  tbody tr:last-of-type td {
    border-bottom: none;
  }

  tfoot td {
    padding: 8px 10px;
    border-top: 1px solid var(--mui-colors-border-card);
    font-weight: 600;
  }
`;

const roundRowCss = css`
  cursor: pointer;

  &:hover {
    background: var(--mui-colors-neutral-5);
  }
`;

/* The keyboard's way into a round. Bare, because the row already carries the styling. */
const expandButtonCss = css`
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
`;

const memberTableCss = css`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  background: var(--mui-colors-neutral-5);

  th,
  td {
    padding: 5px 8px;
    border: none;
  }

  th {
    ${eyebrowCss};
    font-size: 9.5px;
    text-align: left;
  }
`;

const bucketRowCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  font-size: 11.5px;
  border-bottom: 1px dashed var(--mui-colors-border-card);

  &:last-of-type {
    border-bottom: none;
  }
`;

const removedRowCss = css`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 7px 0 7px 9px;
  font-size: 11.5px;
  border-bottom: 1px dashed var(--mui-colors-border-card);
  /* A rule down the left marks the whole block as losses rather than another neutral list. */
  border-left: 2px solid var(--mui-colors-neutral-20);

  &:last-of-type {
    border-bottom: none;
  }
`;

/* Amber only where the money lands nowhere — a merge is a regrouping, not a loss. */
const removedLostCss = css`
  border-left-color: #9a5b00;
`;

const removedNameCss = css`
  font-weight: 600;
  color: var(--mui-colors-text-primary);
`;

const removedDateCss = css`
  font-size: 10.5px;
  color: var(--mui-colors-text-secondary);
  font-variant-numeric: tabular-nums;
`;

const removedFateCss = css`
  color: var(--mui-colors-text-secondary);
`;

const removedCountCss = css`
  font-size: 10.5px;
  color: var(--mui-colors-neutral-40);
  font-variant-numeric: tabular-nums;
`;

const bucketAmountCss = css`
  ${numericCss};
  margin-left: auto;
  color: var(--mui-colors-text-secondary);
`;

const statRowCss = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const statLeadCss = css`
  font-weight: 600;
  color: var(--mui-colors-text-primary);
`;

/* Two or three words, so a column of these scans instead of stacking sentences. */
const reasonCss = css`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--mui-colors-border-card);
  border-radius: 20px;
  padding: 2px 8px;
  font-size: 10.5px;
  color: var(--mui-colors-text-secondary);
  white-space: nowrap;
  cursor: help;
`;

/** The picker's own option shape, so a company picked on the page shows in the modal's picker. */
export function toSearchOption(company: CompanySearchResponseWithRealId): ICompanySearchResponseDataModel {
  return {
    id: String(company.id),
    fields: {
      name: company.fields.name,
      description: company.fields.description ?? '',
      logo: company.fields.logo ?? null,
      website: company.fields.website ?? null,
      type: company.fields.type ?? '',
    },
  };
}

export function RoundCreationPreviewDialog({
  open,
  onClose,
  draft,
  company,
  onPickCompany,
}: {
  open: boolean;
  onClose: () => void;
  draft: RoundCreationSettingsUpdate | null;
  company: CompanySearchResponseWithRealId | null;
  onPickCompany: (company: CompanySearchResponseWithRealId | null) => void;
}) {
  const roundLabel = getRoundLabel();
  const copy = previewCopy(roundLabel);
  const companyId = company?.id ?? null;
  const { data, isFetching, error } = useRoundCreationPreview(companyId, draft);
  // The previous answer stays on screen while the next one loads, so nothing is shown until the
  // response says it is about the company being asked about — it echoes the id, and the settings.
  const result = company && data?.companyId === company.id ? data : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <Stack
        direction='row'
        alignItems='center'
        gap='1rem'
        padding='12px 16px'
        borderBottom='1px solid var(--mui-colors-border-card)'
      >
        <Typography variant='h6' sx={{ fontSize: '15px', whiteSpace: 'nowrap' }}>
          {copy.title}
        </Typography>
        <div style={{ minWidth: '16rem' }}>
          <CompanySearch
            fullWidth
            showPortcoOnly
            // Without these the input blanks on pick and the table stops saying whose numbers it shows.
            showSelectedOption
            showSelectedLogo
            // The page may already have a company picked; the modal opens on that one, not empty.
            initialValue={company ? toSearchOption(company) : undefined}
            placeholder={copy.companyPlaceholder}
            onChange={onPickCompany}
            onClear={() => onPickCompany(null)}
          />
        </div>
        <Typography css={helpCss} sx={{ mt: 0 }}>
          {copy.subtitle}
        </Typography>
        {isFetching && <CircularProgress size='1.25rem' sx={{ ml: 'auto' }} />}
        <IconButton onClick={onClose} sx={{ ml: isFetching ? 0 : 'auto' }} aria-label={copy.close}>
          <CloseRoundedIcon fontSize='small' />
        </IconButton>
      </Stack>

      <DialogContent>
        {companyId === null ? (
          <Alert severity='info'>{copy.pickCompany}</Alert>
        ) : error ? (
          <Alert severity='error'>{getErrorMessage(error, copy.failed)}</Alert>
        ) : !draft ? (
          <Alert severity='info'>{copy.settingsInvalid}</Alert>
        ) : result ? (
          <PreviewResult data={result} roundLabel={roundLabel} companyName={company?.fields.name ?? null} />
        ) : null}
      </DialogContent>

      <DialogActions>
        {/* Which settings produced these numbers: the modal is read hours after the edit, and the
            table can be holding the previous draft's answer while the next one loads. The response
            echoes the settings it ran, so the line describes the table rather than the form. */}
        <Typography css={helpCss} sx={{ mt: 0, mr: 'auto' }}>
          {result ? copy.recap(recapParts(result.settings, roundLabel)) : ''}
        </Typography>
        <Button variant='contained' color='secondary' onClick={onClose}>
          {copy.done}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The draft in one line, in the same words the settings page uses. */
function recapParts(draft: RoundCreationSettingsUpdate, roundLabel: string): string[] {
  const lower = roundLabel.toLowerCase();
  const byLabel: Record<RoundMatchMode, string> = {
    [RoundMatchMode.STAGE]: 'stage only',
    [RoundMatchMode.DATE]: 'date only',
    [RoundMatchMode.STAGE_AND_DATE]: 'stage & date',
  };
  const parts = [`Create a ${lower} by ${byLabel[draft.roundCreationMatchMode]}`];
  if (draft.roundCreationWindowDays != null) {
    parts.push(`${draft.roundCreationWindowDays}d create`);
  }
  parts.push(`attach by ${byLabel[draft.transactionAssignmentMatchMode]}`);
  if (draft.transactionAssignmentWindowDays != null) {
    parts.push(`${draft.transactionAssignmentWindowDays}d attach`);
  }
  if (draft.unassignedGraceDays != null) {
    parts.push(`${draft.unassignedGraceDays}d grace`);
  }
  return parts;
}

function PreviewResult({
  data,
  roundLabel,
  companyName,
}: {
  data: RoundPreviewResponse;
  roundLabel: string;
  companyName: string | null;
}) {
  const copy = previewCopy(roundLabel);
  const delta = data.plannedRoundCount - data.currentRoundCount;
  // One list, because "no round" and "never considered" are the same answer to the client's
  // question: this money is not in a round. The reason chip keeps them apart.
  const skipped: PreviewTransaction[] = [...data.unassigned, ...data.excluded];
  const tracked = data.rounds.reduce((acc, r) => acc + r.transactionIds.length, 0);

  return (
    <Stack>
      <div css={statRowCss}>
        <span css={[pillCss, statLeadCss]}>
          {copy.roundCount(data.plannedRoundCount, FMT.format('integer', data.plannedRoundCount))}
        </span>
        {/* Amber on a change, as the impact strip does: the count moving is the thing to notice. */}
        <span css={[pillCss, delta !== 0 && pillWarnCss]}>
          {deltaLabel(data.plannedRoundCount, data.currentRoundCount, copy)}
        </span>
        <span css={pillCss}>
          {copy.trackedOf(FMT.format('integer', tracked), FMT.format('integer', tracked + skipped.length))}
        </span>
        {skipped.length > 0 && (
          <span css={[pillCss, pillWarnCss]}>{copy.skippedCount(FMT.format('integer', skipped.length))}</span>
        )}
      </div>

      <p css={helpCss} style={{ marginTop: 0, marginBottom: '10px' }}>
        {copy.fromScratch(companyName)}
      </p>

      {data.emptyState && (
        <Alert severity='info' sx={{ mb: '0.75rem' }}>
          {emptyStateLabel(data.emptyState, roundLabel)}
        </Alert>
      )}

      {data.rounds.length > 0 && <RoundsTable rounds={data.rounds} roundLabel={roundLabel} />}

      {data.removed.length > 0 && <RemovedRounds removed={data.removed} roundLabel={roundLabel} />}

      <div css={eyebrowCss} style={{ marginTop: '16px', marginBottom: '4px' }}>
        {copy.notTracked}
        {skipped.length > 0 && ` · ${copy.skippedCount(FMT.format('integer', skipped.length))}`}
      </div>
      {skipped.length === 0 ? (
        <p css={helpCss}>{copy.nothingSkipped}</p>
      ) : (
        skipped.map((row) => (
          <div css={bucketRowCss} key={row.transactionId}>
            <Chip size='small' label={row.transType ?? copy.unknownType} />
            <span>{row.position ?? copy.unknownPosition}</span>
            <span css={helpCss} style={{ marginTop: 0 }}>
              {row.transactionDate ? FMT.format('date', row.transactionDate) : ''}
            </span>
            <span
              css={[reasonCss, row.reason === 'unassigned' && pillWarnCss]}
              title={exclusionReasonLabel(row.reason, roundLabel)}
            >
              {exclusionReasonChip(row.reason, roundLabel)}
            </span>
            <span css={bucketAmountCss}>{FMT.format('currencyShort', row.amount)}</span>
          </div>
        ))
      )}
    </Stack>
  );
}

function RoundsTable({ rounds, roundLabel }: { rounds: PreviewRound[]; roundLabel: string }) {
  const copy = previewCopy(roundLabel);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  const totals = rounds.reduce(
    (acc, r) => ({
      raiseAmount: acc.raiseAmount + (r.raiseAmount ?? 0),
      investmentAmount: acc.investmentAmount + r.investmentAmount,
      shares: acc.shares + r.shares,
    }),
    { raiseAmount: 0, investmentAmount: 0, shares: 0 }
  );
  // Only a kept round carries a raise, and the preview replans from nothing, so today every row
  // comes back without one. A $0 total would assert "no money raised" over a column with no data.
  const anyRaise = rounds.some((r) => r.raiseAmount != null);

  return (
    <div css={tableWrapCss}>
      <table css={tableCss}>
        <thead>
          <tr>
            <th>{copy.columns.source}</th>
            <th>{copy.columns.roundDate}</th>
            <th>{copy.columns.round}</th>
            <th>{copy.columns.stage}</th>
            <th css={numericCss}>{copy.columns.raise}</th>
            <th css={numericCss}>{copy.columns.investment}</th>
            <th css={numericCss}>{copy.columns.shares}</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map((round) => {
            const expanded = open.has(round.key);
            return (
              <Fragment key={round.key}>
                {/* The row stays clickable for the mouse; the button inside it is what a keyboard
                    reaches, and it names the round rather than replacing the row's own name. */}
                <tr css={roundRowCss} onClick={() => toggle(round.key)}>
                  <td>
                    <Chip
                      size='small'
                      variant={round.existing ? 'filled' : 'outlined'}
                      label={round.existing ? copy.sourceKept : copy.sourceNew}
                    />
                  </td>
                  <td>{round.roundDate ? FMT.format('date', round.roundDate) : '—'}</td>
                  <td>
                    <button
                      type='button'
                      css={expandButtonCss}
                      aria-expanded={expanded}
                      aria-label={`${round.name ?? copy.unnamed}: ${expanded ? copy.collapse : copy.expand}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(round.key);
                      }}
                    >
                      {expanded ? (
                        <KeyboardArrowDownRoundedIcon sx={{ fontSize: '12px', mr: '4px' }} />
                      ) : (
                        <KeyboardArrowRightRoundedIcon sx={{ fontSize: '12px', mr: '4px' }} />
                      )}
                      {round.name ?? copy.unnamed}
                    </button>
                    <span css={helpCss} style={{ marginTop: 0, marginLeft: '6px' }}>
                      {copy.transactionCount(FMT.format('integer', round.transactionIds.length))}
                    </span>
                  </td>
                  <td>{round.stageName ?? '—'}</td>
                  <td css={numericCss}>
                    {round.raiseAmount == null ? '—' : FMT.format('currencyShort', round.raiseAmount)}
                  </td>
                  <td css={numericCss}>{FMT.format('currencyShort', round.investmentAmount)}</td>
                  <td css={numericCss}>{FMT.format('integer', round.shares)}</td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <MemberTable members={round.transactions} roundLabel={roundLabel} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>{copy.total}</td>
            <td css={numericCss}>{anyRaise ? FMT.format('currencyShort', totals.raiseAmount) : ''}</td>
            <td css={numericCss}>{FMT.format('currencyShort', totals.investmentAmount)}</td>
            <td css={numericCss}>{FMT.format('integer', totals.shares)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** The rows inside one round: the prototype's sub-table, and the answer to "why this grouping". */
function MemberTable({ members, roundLabel }: { members: PreviewMember[]; roundLabel: string }) {
  const copy = previewCopy(roundLabel);
  const columns = copy.memberColumns;
  return (
    <table css={memberTableCss}>
      <thead>
        <tr>
          <th>{columns.date}</th>
          <th>{columns.type}</th>
          <th>{columns.security}</th>
          <th>{columns.fund}</th>
          <th css={numericCss}>{columns.amount}</th>
          <th css={numericCss}>{columns.shares}</th>
        </tr>
      </thead>
      <tbody>
        {members.map((row) => (
          <tr key={row.transactionId}>
            <td>{row.transactionDate ? FMT.format('date', row.transactionDate) : '—'}</td>
            <td>
              {row.transType ?? copy.unknownType}
              {row.attached && (
                <Chip
                  size='small'
                  variant='outlined'
                  label={copy.attached}
                  title={copy.attachedHelp}
                  sx={{ ml: '6px' }}
                />
              )}
            </td>
            <td>{row.position ?? copy.unknownPosition}</td>
            <td>{row.fundId == null ? '—' : `#${FMT.format('integer', row.fundId)}`}</td>
            <td css={numericCss}>{FMT.format('currencyShort', row.amount)}</td>
            <td css={numericCss}>{FMT.format('integer', row.shares)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The rounds these rules would cost. A negative delta says how many disappear and never which, so
 * this names them and says where their money goes — a merge into a named round reads very
 * differently from a round whose transactions end up in nothing.
 */
function RemovedRounds({ removed, roundLabel }: { removed: PreviewRemovedRound[]; roundLabel: string }) {
  const copy = previewCopy(roundLabel);
  return (
    <>
      <div css={eyebrowCss} style={{ marginTop: '16px', marginBottom: '4px' }}>
        {`${copy.removedTitle} · ${FMT.format('integer', removed.length)}`}
      </div>
      <p css={helpCss} style={{ marginTop: 0, marginBottom: '6px' }}>
        {copy.removedHelp}
      </p>
      {removed.map((round) => (
        <div css={[removedRowCss, round.everythingLeftOut && removedLostCss]} key={round.key}>
          <span css={removedNameCss}>{round.name ?? copy.unnamed}</span>
          <span css={removedDateCss}>{round.roundDate ? FMT.format('date', round.roundDate) : '—'}</span>
          {round.stageName && <span css={reasonCss}>{round.stageName}</span>}
          <span css={removedFateCss}>
            {round.transactionCount === 0
              ? copy.holdsNothing
              : round.everythingLeftOut
                ? copy.everythingLeftOut
                : copy.mergesInto(round.movedTo)}
          </span>
          {round.transactionCount > 0 && (
            <span css={removedCountCss}>
              {copy.txnCount(round.transactionCount, FMT.format('integer', round.transactionCount))}
            </span>
          )}
          <span css={bucketAmountCss}>{FMT.format('currencyShort', round.investmentAmount)}</span>
        </div>
      ))}
    </>
  );
}
